import React from "react";
import { Link } from "react-router-dom";
import { Instagram, MapPin, Mail, Phone, ArrowUpRight } from "lucide-react";
import { BookACallButton } from "@/components/BookACallButton";
import {
    LOGIN_URL,
    BOOK_A_CALL_URL,
    CONTACT,
    SITE,
} from "@/constants/site";
import { SYSTEMS } from "@/data/systems";

export function Footer() {
    const year = new Date().getFullYear();
    return (
        <footer
            className="relative bg-[#0a0b0d] text-white"
            data-testid="site-footer"
        >
            <div className="h-1.5 caution-stripe" aria-hidden="true" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Brand */}
                    <div className="lg:col-span-5">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-3"
                            data-testid="footer-logo-link"
                        >
                            <img
                                src="/assets/mascot_pose1.png"
                                alt=""
                                className="h-14 w-auto"
                                loading="lazy"
                                aria-hidden="true"
                            />
                            <span className="flex flex-col leading-none">
                                <span className="font-display font-extrabold text-xl tracking-tight text-white">
                                    ELEVARA
                                </span>
                                <span className="font-display font-bold text-[11px] tracking-[0.22em] text-[#5BA9F8] mt-1">
                                    SYSTEMS
                                </span>
                            </span>
                        </Link>
                        <p className="mt-5 text-sm text-white/72 leading-relaxed max-w-md">
                            Marketing systems for home service businesses. One
                            plan. Seven systems. No bull*.
                        </p>
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <BookACallButton
                                size="md"
                                testId="book-a-call-footer-button"
                            />
                            <a
                                href={LOGIN_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="login-footer-link"
                                className="h-11 sm:h-12 px-4 inline-flex items-center text-sm font-display font-bold text-white border-2 border-white/20 hover:border-[#188bf6] hover:text-[#188bf6] rounded-xl transition-colors"
                            >
                                Login
                            </a>
                        </div>
                    </div>

                    {/* Sitemap */}
                    <div className="lg:col-span-3">
                        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-white/55">
                            Sitemap
                        </div>
                        <ul className="mt-4 space-y-2.5">
                            <li>
                                <Link
                                    to="/"
                                    data-testid="footer-link-home"
                                    className="text-sm font-display font-semibold text-white/80 hover:text-[#188bf6] transition-colors"
                                >
                                    Home
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/pricing"
                                    data-testid="footer-link-pricing"
                                    className="text-sm font-display font-semibold text-white/80 hover:text-[#188bf6] transition-colors"
                                >
                                    Pricing
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/about"
                                    data-testid="footer-link-about"
                                    className="text-sm font-display font-semibold text-white/80 hover:text-[#188bf6] transition-colors"
                                >
                                    About
                                </Link>
                            </li>
                            <li>
                                <a
                                    href={BOOK_A_CALL_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-link-contact"
                                    className="inline-flex items-center gap-1 text-sm font-display font-semibold text-white/80 hover:text-[#188bf6] transition-colors"
                                >
                                    Contact Us
                                    <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
                                </a>
                            </li>
                            <li className="pt-2">
                                <Link
                                    to="/privacy"
                                    data-testid="footer-link-privacy"
                                    className="text-sm font-display font-medium text-white/55 hover:text-white transition-colors"
                                >
                                    Privacy Policy
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/terms"
                                    data-testid="footer-link-terms"
                                    className="text-sm font-display font-medium text-white/55 hover:text-white transition-colors"
                                >
                                    Terms of Service
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/cookies"
                                    data-testid="footer-link-cookies"
                                    className="text-sm font-display font-medium text-white/55 hover:text-white transition-colors"
                                >
                                    Cookie Policy
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/acceptable-use"
                                    data-testid="footer-link-acceptable-use"
                                    className="text-sm font-display font-medium text-white/55 hover:text-white transition-colors"
                                >
                                    Acceptable Use
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Systems list */}
                    <div className="lg:col-span-2">
                        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-white/55">
                            Systems
                        </div>
                        <ul className="mt-4 space-y-2.5">
                            {SYSTEMS.map((s) => (
                                <li key={s.slug}>
                                    <Link
                                        to={`/systems/${s.slug}`}
                                        data-testid={`footer-system-link-${s.slug}`}
                                        className="text-sm font-display font-medium text-white/72 hover:text-[#188bf6] transition-colors"
                                    >
                                        {s.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Reach us */}
                    <div className="lg:col-span-2">
                        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-white/55">
                            Reach us
                        </div>
                        <ul className="mt-4 space-y-3">
                            <li className="flex items-start gap-2">
                                <Mail
                                    className="w-4 h-4 mt-0.5 text-[#188bf6] shrink-0"
                                    aria-hidden="true"
                                />
                                <a
                                    href={`mailto:${CONTACT.email}`}
                                    data-testid="footer-email-link"
                                    className="text-[13px] font-display font-medium text-white/85 hover:text-[#188bf6] break-all"
                                >
                                    {CONTACT.email}
                                </a>
                            </li>
                            <li className="flex items-start gap-2">
                                <Phone
                                    className="w-4 h-4 mt-0.5 text-[#188bf6] shrink-0"
                                    aria-hidden="true"
                                />
                                <a
                                    href={CONTACT.phoneHref}
                                    data-testid="footer-phone-link"
                                    className="text-[13px] font-display font-medium text-white/85 hover:text-[#188bf6]"
                                >
                                    {CONTACT.phone}
                                </a>
                            </li>
                            <li className="flex items-start gap-2">
                                <Instagram
                                    className="w-4 h-4 mt-0.5 text-[#188bf6] shrink-0"
                                    aria-hidden="true"
                                />
                                <a
                                    href={CONTACT.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-instagram-link"
                                    className="text-[13px] font-display font-medium text-white/85 hover:text-[#188bf6]"
                                >
                                    {CONTACT.instagramHandle}
                                </a>
                            </li>
                            <li className="flex items-start gap-2">
                                <MapPin
                                    className="w-4 h-4 mt-0.5 text-[#188bf6] shrink-0"
                                    aria-hidden="true"
                                />
                                <a
                                    href={CONTACT.gmb}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-gmb-link"
                                    className="text-[13px] font-display font-medium text-white/85 hover:text-[#188bf6]"
                                >
                                    Google My Business
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <p className="text-xs text-white/55 font-display font-medium">
                        © {year} {SITE.name}. All rights reserved.
                    </p>
                    <div className="flex items-center gap-4">
                        <p className="text-[11px] text-white/40 font-display font-bold uppercase tracking-[0.18em]">
                            Built for home service operators.
                        </p>
                        <span className="hidden sm:inline text-white/20" aria-hidden="true">·</span>
                        <a
                            href="https://app.emergent.sh/?utm_source=emergent-badge"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-emergent-link"
                            className="text-[11px] text-white/40 hover:text-white/70 font-display font-medium transition-colors"
                        >
                            Made with Emergent
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default Footer;
